import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Play, Plus, Image, Youtube, Trash2, Heart, Star, Users, Camera, Film, Link as LinkIcon, Upload, Globe, ExternalLink, UserPlus, Handshake, DollarSign, Shield, BarChart3, ClipboardCheck, Award, Target, Sparkles, Music, Mic, Palette, Lightbulb, Video, FileText, Calendar, Phone, MapPin, School, User, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Peace Pilot Logo
const PEACE_PILOT_LOGO = "https://customer-assets.emergentagent.com/job_7852b792-d206-45d9-abcc-26cfb10cae82/artifacts/t4k8boie_8678.jpg";

// Helper function to get full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.includes('.preview.emergentagent.com') || url.includes('.emergent.host')) {
    const path = url.replace(/https?:\/\/[^\/]+/, '');
    return `${BACKEND_URL}${path}`;
  }
  return url;
};

// The 5-Star Events
const fiveStarEvents = [
  { 
    star: 1, 
    title: "The 5-Star Dance Competition", 
    icon: Music,
    color: "from-pink-500 to-rose-600",
    shortDesc: "Solo & Group Dance Performances"
  },
  { 
    star: 2, 
    title: "Peace Over Everything Poetry & Rap Slam", 
    icon: Mic,
    color: "from-purple-500 to-violet-600",
    shortDesc: "Original Poetry & Rap Performances"
  },
  { 
    star: 3, 
    title: "Graphic & Streetwear Design Contest", 
    icon: Palette,
    color: "from-blue-500 to-cyan-600",
    shortDesc: "Original Art & Apparel Designs"
  },
  { 
    star: 4, 
    title: "Youth 'Shark Tank' Sales Pitch", 
    icon: Lightbulb,
    color: "from-amber-500 to-orange-600",
    shortDesc: "Business Ideas & Entrepreneurship"
  },
  { 
    star: 5, 
    title: "60-Second 'Rep Your City' Film Festival", 
    icon: Video,
    color: "from-green-500 to-emerald-600",
    shortDesc: "Short Films Celebrating Milwaukee"
  }
];

export default function PeacePilot() {
  const { isAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeTab, setActiveTab] = useState("photos");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [addPhotoOpen, setAddPhotoOpen] = useState(false);
  const [videoUploadMode, setVideoUploadMode] = useState("youtube");
  const [uploadMode, setUploadMode] = useState("upload");
  const [newVideo, setNewVideo] = useState({ title: "", description: "", youtube_url: "", video_url: "" });
  const [newPhoto, setNewPhoto] = useState({ title: "", description: "", image_url: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const photoFileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);

  useEffect(() => {
    fetchVideos();
    fetchPhotos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/peacepilot/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error("Error fetching videos:", error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/peacepilot/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error("Error fetching photos:", error);
    }
  };

  const handlePhotoFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 1) {
      setSelectedFile(validFiles[0]);
      setSelectedFiles([]);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(validFiles[0]);
      setPreviewUrls([]);
    } else if (validFiles.length > 1) {
      setSelectedFiles(validFiles);
      setSelectedFile(null);
      setPreviewUrl(null);
      const urls = [];
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          urls.push(reader.result);
          if (urls.length === validFiles.length) {
            setPreviewUrls([...urls]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video is too large. Maximum size is 100MB.");
      return;
    }
    
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    
    if (uploadMode === "url") {
      if (!newPhoto.image_url) {
        toast.error("Please enter an image URL");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/peacepilot/photos`, {
          title: newPhoto.title || "Summer Series Photo",
          description: newPhoto.description || "",
          image_url: newPhoto.image_url
        });
        setPhotos([response.data, ...photos]);
        resetPhotoForm();
        toast.success("Photo added successfully!");
      } catch (error) {
        toast.error("Failed to add photo");
      } finally {
        setUploading(false);
      }
      return;
    }
    
    const filesToUpload = selectedFiles.length > 0 ? selectedFiles : (selectedFile ? [selectedFile] : []);
    
    if (filesToUpload.length === 0) {
      toast.error("Please select at least one photo");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      let successCount = 0;
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", newPhoto.title || file.name.split('.')[0]);
        formData.append("description", newPhoto.description || "");
        
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        });
        
        const photoData = {
          title: newPhoto.title || file.name.split('.')[0],
          description: newPhoto.description || "",
          image_url: uploadRes.data.url
        };
        
        const response = await axios.post(`${API}/peacepilot/photos`, photoData);
        setPhotos(prev => [response.data, ...prev]);
        successCount++;
        setUploadProgress(Math.round(((i + 1) / filesToUpload.length) * 100));
      }
      
      resetPhotoForm();
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully!`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUploadVideo = async (e) => {
    e.preventDefault();
    if (videoUploadMode === "upload" && selectedFile) {
      if (!newVideo.title) {
        toast.error("Please enter a title");
        return;
      }
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("title", newVideo.title);
        formData.append("description", newVideo.description || "");
        
        const uploadRes = await axios.post(`${API}/upload/video`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000
        });
        
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: uploadRes.data.url,
          is_upload: true
        };
        
        const response = await axios.post(`${API}/peacepilot/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video uploaded successfully!");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload video. File may be too large.");
      } finally {
        setUploading(false);
      }
    } else if (videoUploadMode === "url") {
      if (!newVideo.title || !newVideo.video_url) {
        toast.error("Please fill in title and video URL");
        return;
      }
      setUploading(true);
      try {
        const videoData = {
          title: newVideo.title,
          description: newVideo.description,
          video_url: newVideo.video_url,
          is_upload: true
        };
        const response = await axios.post(`${API}/peacepilot/videos`, videoData);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    } else {
      if (!newVideo.title || !newVideo.youtube_url) {
        toast.error("Please fill in required fields");
        return;
      }
      setUploading(true);
      try {
        const response = await axios.post(`${API}/peacepilot/videos`, newVideo);
        setVideos([response.data, ...videos]);
        resetVideoForm();
        toast.success("Video added successfully!");
      } catch (error) {
        toast.error("Failed to add video");
      } finally {
        setUploading(false);
      }
    }
  };

  const resetPhotoForm = () => {
    setNewPhoto({ title: "", description: "", image_url: "" });
    setSelectedFile(null);
    setSelectedFiles([]);
    setPreviewUrl(null);
    setPreviewUrls([]);
    setAddPhotoOpen(false);
    setUploadMode("upload");
    setUploadProgress(0);
  };

  const resetVideoForm = () => {
    setNewVideo({ title: "", description: "", youtube_url: "", video_url: "" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setAddVideoOpen(false);
    setVideoUploadMode("youtube");
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/peacepilot/videos/${videoId}`);
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success("Video deleted");
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/peacepilot/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="peace-pilot-page">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d2040] via-[#0a1628] to-[#0a1628]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMC0zMHY2aDZ2LTZoLTZ6bTAgMTJ2Nmg2di02aC02em0wIDEydjZoNnYtNmgtNnptLTEyLTEydjZoNnYtNmgtNnptMCAxMnY2aDZ2LTZoLTZ6bTAtMjR2Nmg2di02aC02em0wIDM2djZoNnYtNmgtNnptLTEyLTI0djZoNnYtNmgtNnptMCAxMnY2aDZ2LTZoLTZ6bTAtMjR2Nmg2di02aC02em0wIDM2djZoNnYtNmgtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src={PEACE_PILOT_LOGO} 
                alt="Milwaukee 5-Star Summer Series" 
                className="w-48 h-48 md:w-64 md:h-64 mx-auto object-contain rounded-full shadow-2xl border-4 border-amber-400/30"
                data-testid="peace-pilot-logo"
              />
            </div>
            
            {/* Title */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-['Outfit'] text-white mb-4 tracking-tight">
              Milwaukee 5-Star Summer Series
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-amber-400 font-semibold mb-2">
              Rules & Permission Slips
            </p>
            <p className="text-slate-400 mb-4">
              Organized by: <span className="text-white">Peace for Change Alliance Inc.</span>
            </p>
            <p className="text-slate-400 mb-6">
              Website: <a href="https://pca-mke.com" className="text-amber-400 hover:underline">pca-mke.com</a>
            </p>

            {/* 5 Star Events Preview */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {fiveStarEvents.map((event) => (
                <div key={event.star} className={`bg-gradient-to-r ${event.color} px-4 py-2 rounded-full flex items-center gap-2`}>
                  <Star className="w-4 h-4 text-white fill-white" />
                  <span className="text-white text-sm font-medium">Star {event.star}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPTION SECTION */}
      <section className="py-16 bg-[#0d1f3c]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-semibold mb-4">
              ABOUT THE PROGRAM
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
              The Milwaukee 5-Star Peace Pilot
            </h2>
          </div>

          {/* Welcome Description */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-10 mb-10">
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Welcome to the <span className="text-amber-400 font-semibold">Milwaukee 5-Star Peace Pilot</span>—a high-energy, high-impact summer youth initiative brought to you by <span className="text-white font-semibold">Peace for Change Alliance Inc.</span> in collaboration with a powerful network of city and community leaders.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Our mission is simple: <span className="text-white font-semibold">to proactively combat summer community violence</span> by investing directly in the genius, creativity, and entrepreneurial spirit of Milwaukee's youth.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed">
              Throughout the summer season, the 5-Star Peace Pilot features <span className="text-amber-400 font-semibold">five distinct competitive events</span>. These competitions give local young people a platform to shine, express themselves, tell their stories, and build real-world skills in a safe, supportive, and celebratory environment. By channeling youth energy into positive civic pride and economic opportunity, we aren't just giving youth something to do—<span className="text-white font-semibold">we are empowering the next generation of Milwaukee leaders.</span>
            </p>
          </div>

          {/* The 5 Stars of the Summer */}
          <div className="mb-10">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              <Star className="w-6 h-6 text-amber-400 fill-amber-400 inline mr-2" />
              The 5 Stars of the Summer
              <Star className="w-6 h-6 text-amber-400 fill-amber-400 inline ml-2" />
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Star 1 */}
              <div className="bg-gradient-to-br from-pink-500/20 to-rose-600/10 border border-pink-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-lg flex items-center justify-center">
                    <Music className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-amber-400 font-bold">Star 1</span>
                </div>
                <h4 className="text-white font-semibold mb-2">Youth Dance Competition</h4>
                <p className="text-slate-400 text-sm">A showcase for movement, teamwork, and high-energy artistic expression.</p>
              </div>

              {/* Star 2 */}
              <div className="bg-gradient-to-br from-purple-500/20 to-violet-600/10 border border-purple-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <Mic className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-amber-400 font-bold">Star 2</span>
                </div>
                <h4 className="text-white font-semibold mb-2">Youth Poetry & Rap Competition</h4>
                <p className="text-slate-400 text-sm">A platform for local lyricists, poets, and emcees to constructively speak their truth through spoken word and hip-hop.</p>
              </div>

              {/* Star 3 */}
              <div className="bg-gradient-to-br from-green-500/20 to-emerald-600/10 border border-green-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <Video className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-amber-400 font-bold">Star 3</span>
                </div>
                <h4 className="text-white font-semibold mb-2">60-Second "Rep Your City" Video Contest</h4>
                <p className="text-slate-400 text-sm">A digital filmmaking challenge tasking youth to capture the beauty, resilience, and positive stories of Milwaukee using just their smartphones.</p>
              </div>

              {/* Star 4 */}
              <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/10 border border-amber-500/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-amber-400 font-bold">Star 4</span>
                </div>
                <h4 className="text-white font-semibold mb-2">Youth "Shark Tank" Entrepreneur Pitch</h4>
                <p className="text-slate-400 text-sm">An economic empowerment competition where aspiring young business owners pitch innovative ideas to foster financial literacy and business acumen.</p>
              </div>

              {/* Star 5 */}
              <div className="bg-gradient-to-br from-blue-500/20 to-cyan-600/10 border border-blue-500/30 rounded-xl p-5 md:col-span-2 lg:col-span-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
                    <Palette className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-amber-400 font-bold">Star 5</span>
                </div>
                <h4 className="text-white font-semibold mb-2">Graphic Art & T-Shirt Design Contest</h4>
                <p className="text-slate-400 text-sm">A visual arts competition where the winning design is professionally printed on custom summer apparel.</p>
              </div>
            </div>
          </div>

          {/* Prize Money Breakdown */}
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-2 border-amber-500/40 rounded-2xl p-6 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 rounded-full mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Official Prize Money Breakdown</h3>
              <p className="text-slate-300">To reward the hard work, talent, and dedication of our participants, each of the five events features a <span className="text-amber-400 font-bold">$750 total cash prize pool</span>.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {/* 1st Place */}
              <div className="bg-gradient-to-br from-amber-400/30 to-yellow-500/20 border border-amber-400/50 rounded-xl p-6 text-center">
                <div className="text-4xl mb-2">🥇</div>
                <p className="text-amber-400 font-bold text-lg">1st Place Winner</p>
                <p className="text-3xl font-bold text-white my-2">$400</p>
                <p className="text-slate-400 text-sm">+ featured spotlight on pca-mke.com</p>
              </div>

              {/* 2nd Place */}
              <div className="bg-white/5 border border-slate-500/30 rounded-xl p-6 text-center">
                <div className="text-4xl mb-2">🥈</div>
                <p className="text-slate-300 font-bold text-lg">2nd Place Winner</p>
                <p className="text-3xl font-bold text-white my-2">$200</p>
                <p className="text-slate-400 text-sm">cash prize</p>
              </div>

              {/* 3rd Place */}
              <div className="bg-white/5 border border-amber-700/30 rounded-xl p-6 text-center">
                <div className="text-4xl mb-2">🥉</div>
                <p className="text-amber-600 font-bold text-lg">3rd Place Winner</p>
                <p className="text-3xl font-bold text-white my-2">$150</p>
                <p className="text-slate-400 text-sm">cash prize</p>
              </div>
            </div>

            {/* Total Investment */}
            <div className="bg-white/10 rounded-xl p-6 text-center">
              <p className="text-slate-300 mb-2">Total Investment in Our Youth</p>
              <p className="text-2xl md:text-3xl font-bold text-white">
                $750 <span className="text-amber-400">×</span> 5 events = <span className="text-amber-400">$3,750</span>
              </p>
              <p className="text-slate-400 text-sm mt-2">
                in direct cash prizes put right back into the hands of Milwaukee's youth to fuel their dreams, businesses, and education!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PART 1: OFFICIAL EVENT RULES */}
      <section className="py-16 bg-[#0d1f3c]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-semibold mb-4">
              PART 1
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
              OFFICIAL EVENT RULES & GUIDELINES
            </h2>
          </div>

          {/* Star 1: Dance Competition */}
          <div className="mb-10 bg-gradient-to-br from-pink-500/10 to-rose-600/5 border border-pink-500/20 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
                <Music className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="text-amber-400 font-bold">Star 1</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">The 5-Star Dance Competition</h3>
              </div>
            </div>

            <div className="space-y-6 text-slate-300">
              <div>
                <h4 className="text-white font-semibold mb-2">Eligibility & Brackets</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Open to youth ages <span className="text-amber-400 font-semibold">12–18</span></li>
                  <li>Divisions: <span className="text-white">Solo Performers</span> and <span className="text-white">Small Groups (2–6 members)</span></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">Performance Rules</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><span className="text-white">Time Limit:</span> Routines must not exceed <span className="text-amber-400">3 minutes and 30 seconds</span>. Exceeding results in a 5-point deduction.</li>
                  <li><span className="text-white">Music Content:</span> All music files must be submitted via email or USB flash drive at least <span className="text-amber-400">48 hours prior</span>. Music must be completely clean, edited, and free of profanity, explicit language, or references to violence and drugs.</li>
                  <li><span className="text-white">Choreography:</span> Movements must be age-appropriate. Suggestive or inappropriate choreography will result in <span className="text-red-400">immediate disqualification</span>.</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">Judging Criteria (100 Total Points)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex justify-between"><span>Choreography & Originality</span><span className="text-amber-400 font-bold">30 pts</span></div>
                  <div className="flex justify-between"><span>Synchronization & Teamwork</span><span className="text-amber-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Stage Presence & Energy</span><span className="text-amber-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Theme Integration (Peace/Unity)</span><span className="text-amber-400 font-bold">20 pts</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Star 2: Poetry & Rap Slam */}
          <div className="mb-10 bg-gradient-to-br from-purple-500/10 to-violet-600/5 border border-purple-500/20 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
                <Mic className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="text-amber-400 font-bold">Star 2</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">The "Peace Over Everything" Poetry & Rap Slam</h3>
              </div>
            </div>

            <div className="space-y-6 text-slate-300">
              <div>
                <h4 className="text-white font-semibold mb-2">Submission & Performance Rules</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Open to youth ages <span className="text-amber-400 font-semibold">12–18</span>. Individual performances only.</li>
                  <li><span className="text-white">Time Limit:</span> Maximum of <span className="text-amber-400">3 minutes</span> per piece. Microphone will be cut off at 3 minutes and 15 seconds.</li>
                  <li><span className="text-white">Originality:</span> All poems and lyrics must be original works. Plagiarism results in <span className="text-red-400">immediate disqualification</span>.</li>
                  <li><span className="text-white">Content Standard:</span> Content must reflect themes of peace, resilience, community pride, overcoming struggle, or unity. <span className="text-red-400">No profanity, derogatory remarks, gang references, or glorification of violence.</span></li>
                  <li><span className="text-white">Audio Backing:</span> Rappers may use an instrumental backing track (must be clean/instrumental-only). No vocal backing tracks. Spoken word poets perform acapella.</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">Judging Criteria (100 Total Points)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex justify-between"><span>Content & Lyricism</span><span className="text-purple-400 font-bold">30 pts</span></div>
                  <div className="flex justify-between"><span>Delivery & Stage Presence</span><span className="text-purple-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Originality & Creativity</span><span className="text-purple-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Adherence to Peace Theme</span><span className="text-purple-400 font-bold">20 pts</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Star 3: Design Contest */}
          <div className="mb-10 bg-gradient-to-br from-blue-500/10 to-cyan-600/5 border border-blue-500/20 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <Palette className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="text-amber-400 font-bold">Star 3</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">The Graphic & Streetwear Design Contest</h3>
              </div>
            </div>

            <div className="space-y-6 text-slate-300">
              <div>
                <h4 className="text-white font-semibold mb-2">Submission Requirements</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Open to youth ages <span className="text-amber-400 font-semibold">12–18</span>. Individual submissions only.</li>
                  <li><span className="text-white">Design Brief:</span> Create an original graphic logo or apparel design that embodies "Milwaukee Unity," "Peace," or the "5-Star Pilot."</li>
                  <li><span className="text-white">Format:</span> Digital (<span className="text-amber-400">PNG, JPEG, PDF at 300 DPI</span>) or hand-drawn on standard white canvas/heavy paper (minimum 8.5" x 11").</li>
                  <li><span className="text-white">Limitations:</span> No copyrighted images, sports logos, or trademarked characters. Design must be <span className="text-amber-400">100% original</span>.</li>
                  <li><span className="text-white">Deadline:</span> All entries must be submitted with completed entry form by <span className="text-amber-400">5:00 PM</span> on the designated deadline date.</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">Judging Criteria (100 Total Points)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex justify-between"><span>Creativity & Concept</span><span className="text-blue-400 font-bold">35 pts</span></div>
                  <div className="flex justify-between"><span>Visual Appeal & Cleanliness</span><span className="text-blue-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Printability for T-Shirts</span><span className="text-blue-400 font-bold">20 pts</span></div>
                  <div className="flex justify-between"><span>Clarity of Peace Message</span><span className="text-blue-400 font-bold">20 pts</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Star 4: Shark Tank */}
          <div className="mb-10 bg-gradient-to-br from-amber-500/10 to-orange-600/5 border border-amber-500/20 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="text-amber-400 font-bold">Star 4</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">The Youth "Shark Tank" Sales Pitch</h3>
              </div>
            </div>

            <div className="space-y-6 text-slate-300">
              <div>
                <h4 className="text-white font-semibold mb-2">Structure & Eligibility</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Open to youth ages <span className="text-amber-400 font-semibold">13–18</span>. Individuals or pairs.</li>
                  <li>Business idea must be <span className="text-white">legal, ethical, and safe</span> for a minor to operate.</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">Presentation Rules</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><span className="text-white">The Pitch:</span> Maximum of <span className="text-amber-400">4 minutes</span> to pitch business concept, product, or service.</li>
                  <li><span className="text-white">Q&A Session:</span> Judges have up to <span className="text-amber-400">3 minutes</span> for questions about business plan, costs, and market demand.</li>
                  <li><span className="text-white">Visuals:</span> Presentation boards, prototypes, or digital slides are highly encouraged but not mandatory.</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">Judging Criteria (100 Total Points)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex justify-between"><span>Innovation & Business Concept</span><span className="text-amber-400 font-bold">30 pts</span></div>
                  <div className="flex justify-between"><span>Market Understanding & Pricing</span><span className="text-amber-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Quality of Pitch & Presentation</span><span className="text-amber-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Viability of Prize Seed Money</span><span className="text-amber-400 font-bold">20 pts</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Star 5: Film Festival */}
          <div className="mb-10 bg-gradient-to-br from-green-500/10 to-emerald-600/5 border border-green-500/20 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <Video className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="text-amber-400 font-bold">Star 5</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">The 60-Second "Rep Your City" Film Festival</h3>
              </div>
            </div>

            <div className="space-y-6 text-slate-300">
              <div>
                <h4 className="text-white font-semibold mb-2">Video Specifications</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Open to youth ages <span className="text-amber-400 font-semibold">12–18</span>. Individual or group (up to 3 people) submissions.</li>
                  <li><span className="text-white">Format:</span> Vertical format (<span className="text-amber-400">9:16 aspect ratio</span>) suitable for TikTok or Instagram Reels.</li>
                  <li><span className="text-white">Duration:</span> Exactly <span className="text-amber-400">30 to 60 seconds</span>. Videos under 30 or over 60 seconds will not be judged.</li>
                  <li><span className="text-white">Content Guidelines:</span> Showcase positive aspects of your neighborhood, community leaders, youth peace efforts, or personal stories of resilience. <span className="text-red-400">No inappropriate language, illegal acts, or dangerous stunts.</span></li>
                  <li><span className="text-white">Copyright:</span> Background music must be royalty-free or properly credited.</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3">Judging Criteria (100 Total Points)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex justify-between"><span>Storytelling & Message Power</span><span className="text-green-400 font-bold">35 pts</span></div>
                  <div className="flex justify-between"><span>Videography & Editing Quality</span><span className="text-green-400 font-bold">25 pts</span></div>
                  <div className="flex justify-between"><span>Creativity & Innovation</span><span className="text-green-400 font-bold">20 pts</span></div>
                  <div className="flex justify-between"><span>Audience Engagement/Impact</span><span className="text-green-400 font-bold">20 pts</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PART 2: PERMISSION SLIP */}
      <section className="py-16 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-semibold mb-4">
              PART 2
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-2">
              UNIVERSAL YOUTH PARTICIPATION & PERMISSION SLIP
            </h2>
            <p className="text-slate-400 text-sm mt-2">
              (Required for all participants under 18 years of age. A separate form must be completed for each event entered.)
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl print:shadow-none">
            {/* Section 1: Participant Information */}
            <div className="mb-8 pb-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                PARTICIPANT INFORMATION
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Participant Full Name</label>
                  <div className="h-10 border-b-2 border-slate-300"></div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Date of Birth</label>
                    <div className="h-10 border-b-2 border-slate-300 flex items-end justify-center text-slate-400 text-sm pb-1">____/____/______</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Age</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Grade</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">School Name</label>
                  <div className="h-10 border-b-2 border-slate-300"></div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Street Address</label>
                  <div className="h-10 border-b-2 border-slate-300"></div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">City</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">State</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Zip Code</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Event Selection */}
            <div className="mb-8 pb-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                EVENT SELECTION <span className="text-sm font-normal text-slate-500">(Please check the event being entered)</span>
              </h3>
              
              <div className="space-y-3">
                {fiveStarEvents.map((event) => (
                  <div key={event.star} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <div className="w-6 h-6 border-2 border-slate-400 rounded flex items-center justify-center">
                      {/* Checkbox placeholder */}
                    </div>
                    <Star className="w-5 h-5 text-amber-500" />
                    <span className="text-[#0F172A]"><span className="font-semibold">Star {event.star}:</span> {event.title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3: Parent/Guardian Consent */}
            <div className="mb-8 pb-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                PARENT/GUARDIAN CONSENT & LIABILITY WAIVER
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-2">
                  <span className="text-slate-600">I,</span>
                  <div className="flex-1 border-b-2 border-slate-300 min-h-[24px]"></div>
                  <span className="text-slate-600">,</span>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  give my explicit permission for my child, named above, to participate in the designated Milwaukee 5-Star Summer Series event organized by Peace for Change Alliance Inc.
                </p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  I understand that participating in these community events may involve physical movement (such as dance) or public presentations. I certify that my child is physically fit and capable of participating safely. On behalf of myself and my child, I hereby <span className="font-semibold">release, waive, and hold harmless</span> Peace for Change Alliance Inc., its officers, employees, volunteers, community partners, and sponsors from any and all liability, claims, or demands for personal injury, sickness, or death, as well as property damage and expenses, of any nature, occurring during or in connection with this event.
                </p>
              </div>
            </div>

            {/* Section 4: Media Release */}
            <div className="mb-8 pb-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                MEDIA RELEASE & PHOTO CONSENT
              </h3>
              
              <p className="text-slate-600 text-sm leading-relaxed">
                I understand that this event will be documented through photographs, video recordings, and digital media for community recap purposes, promotional materials, and social media platforms. I hereby grant Peace for Change Alliance Inc. and its sponsors the absolute right and permission to use, publish, and distribute my child's likeness, voice, or submitted artwork/video content in connection with promoting the 5-Star Peace Pilot program without financial compensation.
              </p>
            </div>

            {/* Section 5: Emergency Medical */}
            <div className="mb-8 pb-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                EMERGENCY MEDICAL AUTHORIZATION
              </h3>
              
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                In the event of a medical emergency, I authorize the event staff and volunteers to secure necessary medical treatment for my child if I cannot be immediately reached.
              </p>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Primary Emergency Contact Name</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Relationship</label>
                      <div className="h-10 border-b-2 border-slate-300"></div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Phone</label>
                      <div className="h-10 border-b-2 border-slate-300"></div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Secondary Contact Name</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Phone</label>
                    <div className="h-10 border-b-2 border-slate-300"></div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Medical Conditions/Allergies Staff Should Know</label>
                  <div className="h-16 border-b-2 border-slate-300"></div>
                </div>
              </div>
            </div>

            {/* Signature Section */}
            <div className="mt-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Parent/Guardian Printed Name</label>
                  <div className="h-10 border-b-2 border-slate-300"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Parent/Guardian Signature</label>
                    <div className="h-16 border-b-2 border-slate-300"></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Date</label>
                    <div className="h-10 border-b-2 border-slate-300 flex items-end justify-center text-slate-400 text-sm pb-1">____/____/______</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Print Button */}
            <div className="mt-8 text-center print:hidden">
              <Button 
                onClick={() => window.print()}
                className="bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-8"
              >
                Print This Form
              </Button>
              <p className="text-sm text-slate-500 mt-3">
                Please print, complete, sign, and bring this form to the event.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-[#0d1f3c]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-6">
            Ready to Compete?
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Print the permission slip above, have your parent/guardian sign it, and bring it to your event!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/peace-architect-apply">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8 py-3">
                <ClipboardCheck className="w-5 h-5 mr-2" />
                Register Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10 px-8 py-3">
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Media Gallery */}
      <section className="py-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">Event Gallery</h2>
              <p className="text-slate-400 mt-1">Photos and videos from the Summer Series</p>
            </div>
            <div className="flex gap-3">
              <Dialog open={addVideoOpen} onOpenChange={setAddVideoOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="add-peacepilot-video-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Video
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#16213e] border-slate-700 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Video</DialogTitle>
                  </DialogHeader>
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      type="button"
                      variant={videoUploadMode === "youtube" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "youtube" ? "bg-amber-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("youtube"); setSelectedFile(null); setPreviewUrl(null); }}
                    >
                      <Youtube className="w-4 h-4 mr-2" />
                      YouTube
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "url" ? "bg-amber-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("url"); setSelectedFile(null); setPreviewUrl(null); setNewVideo({ ...newVideo, youtube_url: "" }); }}
                    >
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Video URL
                    </Button>
                    <Button
                      type="button"
                      variant={videoUploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 min-w-[100px] ${videoUploadMode === "upload" ? "bg-amber-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setVideoUploadMode("upload"); setNewVideo({ ...newVideo, youtube_url: "", video_url: "" }); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </Button>
                  </div>

                  <form onSubmit={handleUploadVideo} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Title *</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Video title"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        required
                      />
                    </div>

                    {videoUploadMode === "youtube" && (
                      <div>
                        <Label className="text-slate-300">YouTube URL *</Label>
                        <Input
                          value={newVideo.youtube_url}
                          onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    )}

                    {videoUploadMode === "url" && (
                      <div>
                        <Label className="text-slate-300">Video URL *</Label>
                        <Input
                          value={newVideo.video_url}
                          onChange={(e) => setNewVideo({ ...newVideo, video_url: e.target.value })}
                          placeholder="https://example.com/video.mp4"
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    )}

                    {videoUploadMode === "upload" && (
                      <div>
                        <Label className="text-slate-300">Select Video *</Label>
                        <input
                          ref={videoFileInputRef}
                          type="file"
                          accept="video/*"
                          onChange={handleVideoFileSelect}
                          className="hidden"
                          id="peacepilot-video-upload"
                        />
                        <label 
                          htmlFor="peacepilot-video-upload"
                          className="mt-2 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors"
                        >
                          <Film className="w-5 h-5" />
                          {selectedFile ? "Change Video" : "Choose Video"}
                        </label>
                        {previewUrl && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <video src={previewUrl} className="max-h-32 mx-auto rounded-lg" controls />
                            <p className="text-sm text-slate-400 text-center mt-2">{selectedFile?.name}</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold" 
                      disabled={uploading}
                    >
                      {uploading ? "Uploading..." : "Add Video"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={addPhotoOpen} onOpenChange={setAddPhotoOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10" data-testid="add-peacepilot-photo-btn">
                    <Camera className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#16213e] border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] text-white">Add Photo</DialogTitle>
                  </DialogHeader>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      type="button"
                      variant={uploadMode === "upload" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "upload" ? "bg-amber-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("upload"); setNewPhoto({ ...newPhoto, image_url: "" }); }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      From Phone
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === "url" ? "default" : "outline"}
                      className={`flex-1 ${uploadMode === "url" ? "bg-amber-500 text-black" : "border-slate-600 text-white"}`}
                      onClick={() => { setUploadMode("url"); setSelectedFile(null); setSelectedFiles([]); setPreviewUrl(null); setPreviewUrls([]); }}
                    >
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Image URL
                    </Button>
                  </div>

                  <form onSubmit={handleUploadPhoto} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Title</Label>
                      <Input
                        value={newPhoto.title}
                        onChange={(e) => setNewPhoto({ ...newPhoto, title: e.target.value })}
                        placeholder="Photo title (optional)"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                      />
                    </div>

                    {uploadMode === "url" ? (
                      <div>
                        <Label className="text-slate-300">Image URL *</Label>
                        <Input
                          value={newPhoto.image_url}
                          onChange={(e) => setNewPhoto({ ...newPhoto, image_url: e.target.value })}
                          placeholder="https://example.com/image.jpg"
                          className="mt-1 bg-slate-800 border-slate-600 text-white"
                          required
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-slate-300">Select Photo(s) *</Label>
                        <input
                          ref={photoFileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoFileSelect}
                          className="hidden"
                          id="peacepilot-photo-upload"
                        />
                        <label 
                          htmlFor="peacepilot-photo-upload"
                          className="mt-2 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors"
                        >
                          <Camera className="w-5 h-5" />
                          {selectedFile || selectedFiles.length > 0 ? "Change Photo(s)" : "Choose Photo(s)"}
                        </label>
                        
                        {previewUrl && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <img src={previewUrl} alt="Preview" className="max-h-40 mx-auto rounded-lg object-contain" />
                            <p className="text-sm text-slate-400 text-center mt-2">{selectedFile?.name}</p>
                          </div>
                        )}
                        
                        {previewUrls.length > 0 && (
                          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
                            <div className="grid grid-cols-3 gap-2">
                              {previewUrls.slice(0, 6).map((url, idx) => (
                                <img key={`preview-${idx}-${url.substring(0, 20)}`} src={url} alt={`Preview ${idx + 1}`} className="w-full h-20 object-cover rounded-lg" />
                              ))}
                            </div>
                            <p className="text-xs text-green-400 text-center mt-2">{selectedFiles.length} photos selected</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-300">Description</Label>
                      <Textarea
                        value={newPhoto.description}
                        onChange={(e) => setNewPhoto({ ...newPhoto, description: e.target.value })}
                        placeholder="Brief description"
                        className="mt-1 bg-slate-800 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>

                    {uploading && uploadProgress > 0 && (
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold" 
                      disabled={uploading}
                    >
                      {uploading ? `Uploading... ${uploadProgress}%` : "Add Photo"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
            <TabsList className="bg-slate-800/50">
              <TabsTrigger value="photos" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                <Image className="w-4 h-4 mr-2" />
                Photos ({photos.length})
              </TabsTrigger>
              <TabsTrigger value="videos" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                <Play className="w-4 h-4 mr-2" />
                Videos ({videos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="photos" className="mt-6">
              {photos.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No photos yet. Be the first to add one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div 
                      key={photo.id} 
                      className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer"
                      onClick={() => setSelectedPhoto(photo)}
                      data-testid={`peacepilot-photo-${photo.id}`}
                    >
                      <img 
                        src={getImageUrl(photo.image_url)} 
                        alt={photo.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {photo.is_featured && (
                        <div className="absolute top-2 left-2 bg-amber-500 text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Star className="w-3 h-3" /> Featured
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="text-white font-medium text-sm line-clamp-2">{photo.title}</h3>
                        </div>
                      </div>
                      {isAuthenticated && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="videos" className="mt-6">
              {videos.length === 0 ? (
                <div className="text-center py-12">
                  <Play className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No videos yet. Be the first to add one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <div 
                      key={video.id} 
                      className="group relative bg-slate-800/50 rounded-xl overflow-hidden cursor-pointer"
                      onClick={() => setSelectedVideo(video)}
                      data-testid={`peacepilot-video-${video.id}`}
                    >
                      <div className="aspect-video relative">
                        {video.is_upload ? (
                          video.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                            <video src={getImageUrl(video.video_url)} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center">
                              <Globe className="w-12 h-12 text-slate-500 mb-2" />
                              <span className="text-xs text-slate-500">External Video</span>
                            </div>
                          )
                        ) : (
                          <img 
                            src={video.thumbnail_url} 
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                          <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
                            <Play className="w-8 h-8 text-black ml-1" />
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-white group-hover:text-amber-400 transition-colors line-clamp-2">
                          {video.title}
                        </h3>
                      </div>
                      {isAuthenticated && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); handleDeleteVideo(video.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Footer */}
      <section className="py-12 bg-[#0d1f3c] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-amber-400 font-semibold mb-2">Peace for Change Alliance Inc.</p>
          <p className="text-slate-300 mb-1">Tracey Dent, Founder</p>
          <p className="text-slate-400 text-sm">Built for Milwaukee. Led by Milwaukee.</p>
        </div>
      </section>

      {/* Photo Viewer Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#1a1a2e] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedPhoto?.title}</DialogTitle>
          {selectedPhoto && (
            <div>
              <img 
                src={getImageUrl(selectedPhoto.image_url)} 
                alt={selectedPhoto.title}
                className="w-full max-h-[70vh] object-contain"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedPhoto.title}</h3>
                {selectedPhoto.description && (
                  <p className="text-slate-400">{selectedPhoto.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 bg-[#1a1a2e] border-slate-700 overflow-hidden">
          <DialogTitle className="sr-only">{selectedVideo?.title}</DialogTitle>
          {selectedVideo && (
            <div>
              {selectedVideo.is_upload ? (
                selectedVideo.video_url?.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i) ? (
                  <video src={getImageUrl(selectedVideo.video_url)} controls autoPlay className="w-full max-h-[70vh]" />
                ) : (
                  <div className="aspect-video bg-slate-900 flex flex-col items-center justify-center p-8">
                    <Globe className="w-16 h-16 text-slate-500 mb-4" />
                    <p className="text-slate-400 text-center mb-6">This video is hosted on an external website</p>
                    <a 
                      href={selectedVideo.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black px-6 py-3 rounded-full font-semibold transition-colors"
                    >
                      <ExternalLink className="w-5 h-5" />
                      Watch on External Site
                    </a>
                  </div>
                )
              ) : (
                <div className="youtube-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1`}
                    title={selectedVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedVideo.title}</h3>
                {selectedVideo.description && (
                  <p className="text-slate-400">{selectedVideo.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
